import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import MapView from './components/MapView';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import IconButton from '../../components/IconButton';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import ChangePhotosIcon from '../../assets/formIcons/photosSmall.svg';
import ChangeOrgIcon from '../../assets/formIcons/changeOrg.svg';
import EmptyHeight from '../../components/EmptyHeight';
import { nonHourDayTypes } from '../AddOrganization/components/DaysSection';

function ViewSingleOrganization() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('id');

  const [gettingLoading, setGettingLoading] = useState(true);
  const [gettingError, setGettingError] = useState('');
  const [singleOrganization, setSingleOrganization] = useState(null);

  const isOnlineWorking = useMemo(() => Boolean(singleOrganization?.category?.is_online), [singleOrganization]);

  const onCancelClick = () => {
    navigate('/organizations/view');
  };

  const onEditPhotoClick = () => {
    navigate(`/organizations/edit-photos?id=${singleOrganization._id}`);
  };

  const onEditClick = () => {
    navigate(`/organizations/edit?id=${singleOrganization._id}`);
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getOrganization,
      payload: { id: organizationId },
      warnErrorText: 'while getting single organization',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    setSingleOrganization(data.organization);

    /*    setTimeout(() => {
      setCoordinates(selectedOrganization.coordinates);
    }, */
  };

  useEffect(() => {
    initData().then();
  }, []);

  return (
    <div className={styles.wrapper}>
      <PageTitle
        text="Просмотр"
        RightComponent={(
          <div className={styles.buttonsRow}>
            <IconButton
              svgPath={CancelEditingIcon}
              tooltipText="Отменить"
              onClick={onCancelClick}
            />
            <IconButton
              svgPath={ChangePhotosIcon}
              tooltipText="Изменить фото"
              onClick={onEditPhotoClick}
            />
            <IconButton
              svgPath={ChangeOrgIcon}
              tooltipText="Редактировать"
              onClick={onEditClick}
            />
          </div>
        )}
      />
      {gettingLoading
        ? <p className={styles.infoText}>Загрузка</p>
        : gettingError
          ? <p className={styles.infoText}>{gettingError}</p>
          : singleOrganization ? (
            <div className={styles.container}>
              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Владелец</p>
                    <p className={styles.infoTitle}>{moment(singleOrganization.user.createdAt).format('DD.MM.YYYY')}</p>
                  </div>
                  <p className={styles.text}>
                    ID OWN:
                    {' '}
                    {singleOrganization.user._id}
                  </p>
                  <EmptyHeight height={16} />
                  <p className={styles.text}>
                    {singleOrganization.user.email}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Категории</p>
                  </div>
                  <p className={styles.textBold}>
                    {singleOrganization.category.name}
                  </p>
                  <EmptyHeight height={16} />
                  <p className={styles.text}>
                    {singleOrganization.specialization}
                  </p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>
                    {singleOrganization.sub_categories.map((i) => i.name).join(', ')}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Описание</p>
                  </div>
                  <p className={styles.text}>
                    {singleOrganization.description}
                  </p>
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Местоположение</p>
                  </div>
                  <p className={styles.textBold}>
                    {singleOrganization.country.name}
                  </p>
                  <EmptyHeight height={16} />
                  <p className={styles.text}>
                    {singleOrganization.city.name}
                  </p>
                  <EmptyHeight height={16} />
                  <p className={styles.text}>
                    {singleOrganization.additional_cities?.map((i) => i.name).join(', ')}
                  </p>

                  {!isOnlineWorking && (
                  <>
                    <EmptyHeight height={16} />
                    <MapView
                      city={singleOrganization.city}
                      coordinates={singleOrganization.coordinates}
                      setCoordinates={() => {}}
                      defaultZoom={12}
                    />
                    <EmptyHeight height={16} />
                    <p className={styles.textGray}>{singleOrganization.address}</p>
                  </>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Контакты</p>
                  </div>
                  <p className={styles.textGray}>Телефон</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.phone_number || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>Instagram</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.instagram || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>Telegram</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.telegram || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>WhatsApp</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.whatsapp || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>Viber</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.viber || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>Facebook</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.facebook || '____'}</p>
                  <EmptyHeight height={16} />
                  <p className={styles.textGray}>Сайт</p>
                  <EmptyHeight height={8} />
                  <p className={styles.text}>{singleOrganization.contacts.website || '____'}</p>
                  <EmptyHeight height={16} />
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Режим работы</p>
                  </div>
                  <div className={styles.row}>
                    <p className={styles.text}>ПН</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.monday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.monday]?.text || singleOrganization.work_time.monday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>ВТ</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.tuesday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.tuesday]?.text || singleOrganization.work_time.tuesday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>СР</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.wednesday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.wednesday]?.text || singleOrganization.work_time.wednesday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>ЧТ</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.thursday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.thursday]?.text || singleOrganization.work_time.thursday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>ПТ</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.friday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.friday]?.text || singleOrganization.work_time.friday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>СБ</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.saturday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.saturday]?.text || singleOrganization.work_time.saturday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                  <div className={styles.row}>
                    <p className={styles.text}>ВС</p>
                    <p
                      className={styles.text}
                      style={{ color: nonHourDayTypes[singleOrganization.work_time.sunday]?.color }}
                    >
                      {nonHourDayTypes[singleOrganization.work_time.sunday]?.text || singleOrganization.work_time.sunday}
                    </p>
                  </div>
                  <EmptyHeight height={16} />
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Организация</p>
                    <p className={styles.infoTitle}>{moment(singleOrganization.createdAt).format('DD.MM.YYYY')}</p>
                  </div>
                  <p className={styles.text}>
                    {singleOrganization._id}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Фото</p>
                  </div>
                  <div className={styles.list}>
                    {[...singleOrganization.carousel_photos, ...singleOrganization.owner_photos].map((photo) => (
                      <img className={styles.photo} key={photo._id} src={Api.url + photo.preview_url} alt="ph" />
                    ))}
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Тэги</p>
                  </div>
                  <div className={styles.list}>
                    {singleOrganization.tags.map((tag, index) => (
                      <p key={index} className={styles.tag}>{tag}</p>
                    ))}
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Языки</p>
                  </div>
                  <div className={styles.list}>
                    {singleOrganization.languages.map((language) => (
                      <img className={styles.language} key={language._id} src={Api.url + language.flag_url} alt="lng" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
    </div>

  );
}

export default ViewSingleOrganization;
